
import { defineComponent } from 'vue';
import { DateTime, Duration } from 'luxon';
import _range from 'lodash/range';
import { isToday, isTomorrow } from '@/shared/helpers';
import { mapActions, mapGetters } from 'vuex';
import Load from '@/types/Load';
import { ActionTypes } from '@/store/actionTypes';
import SomethingWrongOverlay from '@/components/SomethingWrongOverlay.vue';

export default defineComponent({
    components: {
        SomethingWrongOverlay,
    },
    emits: ['onPtaSelected'],
    data(): {
        controlMode: string;
        selectedPta: DateTime;
        timeOptions: DateTime[];
        dateOptions: DateTime[];
        customHour: number;
        customMinute: number;
        customDate: DateTime;
        meridiem: 'am' | 'pm';
        commitTimeout: number;
    } {
        return {
            timeOptions: [],
            dateOptions: [],
            customHour: -1,
            customMinute: -1,
            meridiem: 'am',
            controlMode: CONTROL_MODE.SELECT,
            selectedPta: DateTime.invalid('uninitialized'),
            customDate: DateTime.invalid('uninitialized'),
            commitTimeout: 10,
        };
    },
    computed: {
        ...mapGetters({
            driver: 'getDriver',
            activeLoad: 'getActiveLoad',
            commitError: 'getError',
        }),
        isSelectMode() {
            return this.controlMode === CONTROL_MODE.SELECT;
        },
        isSetTimeMode() {
            return this.controlMode === CONTROL_MODE.SET_TIME;
        },
        isSetDateMode() {
            return this.controlMode === CONTROL_MODE.SET_DATE;
        },
        isNotifyPlannerMode() {
            return this.controlMode === CONTROL_MODE.NOTIFY_PLANNER;
        },
        isReturnHomeMode() {
            return this.controlMode === CONTROL_MODE.RETURN_HOME;
        },
        isAmSelected() {
            return this.meridiem === 'am';
        },
        isPmSelected() {
            return this.meridiem === 'pm';
        },
        selectedDateText() {
            const selectedDate =
                !this.customDate || !this.customDate.isValid
                    ? DateTime.now()
                    : this.customDate;

            const formattedDate = selectedDate.toFormat('EEEE, MMMM d');

            if (isToday(selectedDate)) {
                return `Today, ${formattedDate}`;
            }

            if (isTomorrow(selectedDate)) {
                return `Tomorrow, ${formattedDate}`;
            }

            return formattedDate;
        },
        dateText() {
            const selectedDate =
                !this.selectedPta || !this.selectedPta.isValid
                    ? DateTime.now()
                    : this.selectedPta;

            return selectedDate.toFormat('EEEE, MMMM d');
        },
        timeText() {
            const selectedDate =
                !this.selectedPta || !this.selectedPta.isValid
                    ? DateTime.now()
                    : this.selectedPta;

            return selectedDate.toFormat('h:mm a');
        },
        customPta() {
            // this is so bad and I'm sorry
            const hour =
                this.meridiem === 'pm'
                    ? this.customHour === 12
                        ? 12
                        : this.customHour + 12
                    : this.customHour === 12
                    ? 0
                    : this.customHour;

            const customDate =
                !this.customDate || !this.customDate.isValid
                    ? DateTime.now()
                    : this.customDate;

            return customDate.set({ hour, minute: this.customMinute });
        },
        setTimeHourError() {
            return this.customHour < 1 || this.customHour > 12;
        },
        setTimeMinuteError() {
            return this.customMinute < 0 || this.customMinute > 59;
        },
        setTimePastError() {
            return this.customPta < DateTime.now();
        },
        disableSetTimeSubmit() {
            return (
                this.setTimeHourError ||
                this.setTimeMinuteError ||
                this.setTimePastError
            );
        },
        formattedMinute() {
            const formatter = Intl.NumberFormat('en-US', {
                minimumIntegerDigits: 2,
            });

            return formatter.format(this.customMinute);
        },
        todayOrTomorrowText() {
            if (isToday(this.selectedPta)) {
                return 'Today';
            }

            if (isTomorrow(this.selectedPta)) {
                return 'Tomorrow';
            }

            return '';
        },
        messageError() {
            return this.commitError
                ? `${this.commitError.name} ${this.commitError.statusCode}: ${this.commitError.message}`
                : '';
        },
    },
    methods: {
        ...mapActions({ updatePta: 'updateDriverPta' }),
        initializeFromActiveLoad() {
            if (!this.driver || typeof this.activeLoad === 'undefined') {
                return;
            }

            const { interval, initial } = getTimeOptionConfig(this.activeLoad);
            const timeOptions = _range(8)
                .map((iteration, index) =>
                    index === 0
                        ? initial
                        : initial.plus(
                              interval.mapUnits((unit) => unit * iteration)
                          )
                )
                .map(roundTimeToNearestHalfHour);
            const dateOptions = _range(1, 8).map((n) =>
                DateTime.now().startOf('day').plus({ days: n })
            );

            const selectedPta = this.driver?.pta;
            const now = DateTime.now();

            const defaultHour = (now.hour % 12) + 1;
            const defaultMinute = now.minute;
            const defaultMeridiem = now.hour >= 12 ? 'pm' : 'am';

            let customHour = defaultHour;
            let customMinute = defaultMinute;
            let meridiem = defaultMeridiem;

            if (
                selectedPta &&
                (!isNaN(selectedPta.hour) || !isNaN(selectedPta.minute))
            ) {
                customHour = !isNaN(selectedPta.hour)
                    ? (selectedPta.hour % 12) + 1
                    : defaultHour;

                customMinute = !isNaN(selectedPta.minute)
                    ? selectedPta.minute
                    : defaultMinute;

                meridiem = !isNaN(selectedPta.hour)
                    ? selectedPta.hour >= 12
                        ? 'pm'
                        : 'am'
                    : defaultMeridiem;
            }

            Object.assign(this, {
                selectedPta,
                timeOptions,
                dateOptions,
                customHour,
                meridiem,
                customDate: selectedPta,
                customMinute,
            });
        },
        openSelectMode() {
            this.controlMode = CONTROL_MODE.SELECT;
        },
        openSetTimeMode() {
            this.controlMode = CONTROL_MODE.SET_TIME;
        },
        openSetDateMode() {
            this.controlMode = CONTROL_MODE.SET_DATE;
        },
        openNotifyPlannerMode() {
            this.controlMode = CONTROL_MODE.NOTIFY_PLANNER;
        },
        openReturnHomeMode() {
            this.controlMode = CONTROL_MODE.RETURN_HOME;
        },
        handleCustomOptionClicked() {
            this.selectedPta = this.selectedPta.set({ hour: this.customHour });
            this.openSetTimeMode();
        },
        handleHourInput(e: Event) {
            const { value: rawValue } = e.target as HTMLInputElement;
            const value = parseInt(rawValue);

            if (isNaN(value)) {
                return;
            }

            this.customHour = value;
        },
        handleMinuteInput(e: Event) {
            const { value: rawValue } = e.target as HTMLInputElement;
            const value = parseInt(rawValue);

            if (isNaN(value)) {
                return;
            }

            this.customMinute = value;
        },
        handleTimeOptionClicked(dateTime: DateTime) {
            this.selectedPta = dateTime;
            this.openNotifyPlannerMode();
        },
        handleNowTimeOptionClicked() {
            this.selectedPta = DateTime.now();
            this.openNotifyPlannerMode();
        },
        handleSetTimeDoneClicked() {
            this.selectedPta = this.customPta;
            this.openNotifyPlannerMode();
        },
        handleDateOptionClicked(dateTime: DateTime) {
            this.customDate = dateTime;
            this.openSetTimeMode();
        },
        handleTodayDateOptionClicked() {
            const today = DateTime.now().startOf('day');

            this.handleDateOptionClicked(today);
        },
        handleNotifyPlannerChangeTimeClicked() {
            this.cancelCommitAfterTimeout();
            this.initializeFromActiveLoad();
            this.openSelectMode();
        },
        commitUpdateAfterTimeout() {
            (this as any).commitTimeoutId = setTimeout(
                this.countDownAndCommitIfTimedOut,
                1000
            );
        },
        cancelCommitAfterTimeout() {
            clearTimeout((this as any).commitTimeoutId);
            this.commitTimeout = 10;
        },
        countDownAndCommitIfTimedOut() {
            this.commitTimeout -= 1;

            if (this.commitTimeout === 0) {
                this.commitChanges();
            } else {
                this.commitUpdateAfterTimeout();
            }
        },
        commitChanges() {
            this.cancelCommitAfterTimeout();

            const currentPta =
                !this.selectedPta || !this.selectedPta.isValid
                    ? DateTime.now()
                    : this.selectedPta;

            this.$store.dispatch(ActionTypes.UPDATE_DRIVER_PTA, currentPta);

            this.openReturnHomeMode();
        },
    },
    watch: {
        activeLoad: {
            handler() {
                this.initializeFromActiveLoad();
            },
            immediate: true,
        },
        controlMode: {
            handler(value) {
                if (value === CONTROL_MODE.NOTIFY_PLANNER) {
                    this.commitUpdateAfterTimeout();
                } else {
                    this.cancelCommitAfterTimeout();
                }
            },
            immediate: true,
        },
    },
    created() {
        if (this.$route.query.mode === CONTROL_MODE.RETURN_HOME) {
            this.$router.replace({ query: { mode: undefined } });
            this.commitChanges();
        }
    },
});

const CONTROL_MODE = {
    // choice of some pre-defined times
    SELECT: 'select',
    // enter your own hour and minute
    SET_TIME: 'set_time',
    // enter your own day
    SET_DATE: 'set_date',
    // commit to API with ten second timer
    NOTIFY_PLANNER: 'notify_planner',
    // instructions to press home button
    RETURN_HOME: 'return_home',
};

interface TimeOptionConfig {
    initial: DateTime;
    interval: Duration;
}

function getTimeOptionConfig(activeLoad: Load | null): TimeOptionConfig {
    let earliestArrivalTime = activeLoad?.destination.earliestArrivalTime;
    const interval = Duration.fromObject({ minutes: 30 });

    if (earliestArrivalTime && earliestArrivalTime < DateTime.now()) {
        earliestArrivalTime = DateTime.now();
    }

    if (earliestArrivalTime) {
        return {
            interval,
            initial: earliestArrivalTime.plus({ minutes: 30 }),
        };
    } else {
        return {
            interval,
            initial: DateTime.now(),
        };
    }
}

function roundTimeToNearestHalfHour(dateTime: DateTime) {
    const { minute } = dateTime;

    if (minute >= 30) {
        if (minute > 45) {
            return dateTime.set({ minute: 60 });
        }

        return dateTime.set({ minute: 30 });
    }

    if (minute > 15) {
        return dateTime.set({ minute: 30 });
    }

    return dateTime.set({ minute: 0 });
}
